import type {PanelOptions} from "../../blink-sdk/PanelOptionsLogic";
import type {StyleRuleObject} from "../../stem-core/ui/Style";
import {iFrameConnection} from "./IFrameConnection";
import {IFrameMessages} from "../../blink-sdk/messaging/IFrameMessages";
import {
    GATE_TYPE,
    PANEL_FRAME,
    PANEL_TYPE
} from "../../blink-sdk/Constants";
import {
    ensureArray,
    ensureInEnum,
    ensureNonRecursivePlainObject,
    ensurePlainObject,
    isHTTPSUrl,
    isValidFontFace
} from "../panel/Security";
import {BlinkGlobal, cleanObject, toArray} from "../UtilsLib";
import {Dispatchable} from "../../stem-core/base/Dispatcher";
import {Product} from "../../client/state/ProductStore";
import {filterStoreByMerchantAndLooseIdArray} from "../../client/state/misc/Filter";


// The SDK hands the iframe its panel options, so those ride in from PanelOptions rather than being
// restated here - 25 of them were `any` in this list while typed there. What stays below is what
// PanelOptions does not declare: the iframe's own state, and the extras the SDK or a merchant adds
export interface IFrameParams extends Partial<PanelOptions> {
    forceHideWallet: boolean;
    // Sent by IFrameElement.getIFrameParams, scaled to the merchant page; getWindowWidth reads the width
    screen: {width: number; height: number; isEmulatorScreen?: boolean};
    allowUserToCoverFees: boolean;
    merchantPageUrl: string;
    merchantPageReferrerUrl: string;
    preferredEmail: string;
    activePanel: boolean;
    // Keyed by subscription offer id. Merchant-authored with no backend counterpart, so this is what the
    // two subscribe card components read: the header, the title and price overrides, and the perk list
    subscriptionOfferConfigs?: Record<string, {
        bestValue?: boolean;
        customHeaderType?: string;
        customHeaderColor?: string;
        customHeaderTitle?: string;
        customHeaderTitleColor?: string;
        useCycleDurationAsName?: boolean;
        useOfferAsTitle?: boolean;
        // A message template, given the price and the cycle duration as its two arguments
        pricesBreakdown?: string;
        useOfferNameAsPriceBreakdown?: boolean;
        perks?: string[];
    }>;
    // The journey that opened the panel, as the SDK's Journey declares it
    journeyId?: string | number;
    journeyAlias?: string;
    // amount is a value in the offer's currency, or "any" to preselect nothing
    donationOverrides?: {frequency?: string; amount?: number | string};
    skipAuth?: boolean;
    footerSeparatorStyle?: StyleRuleObject;
    footerTextStyle?: StyleRuleObject;
    // The widget's, pushed down the same way WidgetPartOptions describes on the SDK side
    visible?: boolean;
    positionBottom?: number;
    // Panel presentation, all set from the SDK's panel options
    buttonStyle?: StyleRuleObject;
    customHeader?: StyleRuleObject;
    bestValueHeader?: StyleRuleObject;
    footerText?: string;
    subscribeImageLogo?: string;
    hasStepIndicator?: boolean;
    // Flow behaviour
    disableUserClose?: boolean;
    urlToRedirectOnSuccess?: string;
}

// TODO @cleanup remove all getters
class IFrameState extends Dispatchable {
    iframeParams: IFrameParams = {
        forceHideWallet: false,
        gateType: null,
        screen: null,
        allowUserToCoverFees: null,
        merchantPageUrl: "",
        merchantPageReferrerUrl: "",
        preferredEmail: null,
        activePanel: false,
    };

    init(iframeParams: IFrameParams) {
        this.iframeParams = iframeParams;

        BlinkGlobal.iFrameState = this;

        iFrameConnection.addListener(IFrameMessages.UPDATE_APP_STATE, ({iframeParams}: {iframeParams: IFrameParams}) => {
            this.iframeParams = iframeParams;
            this.dispatchChange(iframeParams);
        });

        iFrameConnection.addListener(IFrameMessages.UPDATE_PANEL_CONTENT, ({contentAST}: {contentAST: any}) => {
            this.iframeParams.contentAST = contentAST;
            this.dispatch("customPanelContentUpdate");
        });
    }

    get activePanel() {
        return this.iframeParams.activePanel;
    }

    get forceHideWallet() {
        return this.iframeParams.forceHideWallet;
    }

    get preferredEmail() {
        return this.iframeParams.preferredEmail;
    }

    // These panel options get transferred from panel to modal when the flow switches iframe.
    getPanelToFlowOptions() {
        // TODO: Probably transfer unevaluated context? Not sure.
        // TODO @flow @cleanup there need to be a consistent rule on what gets passed from panel to flow.
        const copiedFields: (keyof IFrameParams)[] = ["subscriptionOffer", "donationOffer", "newsletter", "messages", "subscriptionOfferConfigs"];
        let inheritedOptions: any = {};
        for (const key of copiedFields) {
            if (this.iframeParams[key]) {
                inheritedOptions[key] = this.iframeParams[key];
            }
        }

        return {
            ...inheritedOptions,
            metadata: this.getMetadata(),
            onFlowCheckpoint: this.iframeParams.onFlowCheckpoint,
        };
    }

    getMetadata() {
        return cleanObject({
            ...this.iframeParams.metadata,
            // Make sure to only overwrite what we explicitly have
            ...cleanObject({
                journeyId: this.iframeParams.journeyId,
                journeyAlias: this.iframeParams.journeyAlias,
            })
        });
    }

    get donationOverrides() {
        return this.iframeParams.donationOverrides || {};
    }

    get panelType() {
        // TODO @Mihai fucking silent conversion, warn me about it in the console. Fuck.
        return ensureInEnum(this.iframeParams.type, PANEL_TYPE);
    }

    get skipCTA() {
        return !!this.iframeParams.skipCTA;
    }

    get skipAuth() {
        return !!this.iframeParams.skipAuth;
    }

    get editRecurring() {
        return !!this.iframeParams.editRecurring;
    }

    get frame() {
        return ensureInEnum(this.iframeParams.frame, PANEL_FRAME);
    }

    get gateType() {
        return ensureInEnum(this.iframeParams.gateType, GATE_TYPE);
    }

    get subscriptionOffer() {
        const {subscriptionOffer} = this.iframeParams;
        return subscriptionOffer ? (Array.isArray(subscriptionOffer) ? subscriptionOffer : [subscriptionOffer]) : null;
    }

    get newsletter() {
        const {newsletter} = this.iframeParams;
        return newsletter && toArray(newsletter);
    }

    get authSocialProviders() {
        return ensureArray(this.iframeParams.authSocialProviders).map(value => value.toLowerCase());
    }

    get theme() {
        return ensurePlainObject(this.iframeParams.theme);
    }

    get messages() {
        return ensureNonRecursivePlainObject(this.iframeParams.messages);
    }

    get title() {
        return this.iframeParams.title;
    }

    get textStyle() {
        // TODO @cleanup What the fuck is this?
        return ensurePlainObject(this.iframeParams.textStyle);
    }

    get footerSeparatorStyle() {
        return ensurePlainObject(this.iframeParams.footerSeparatorStyle);
    }

    get footerTextStyle() {
        return ensurePlainObject(this.iframeParams.footerTextStyle);
    }

    get allowUserToCoverFees() {
        // TODO: Make sure you support the fees checkbox while editing after some model update.
        return this.iframeParams.allowUserToCoverFees;
    }

    get actionButtonStyle() {
        return ensurePlainObject(this.iframeParams.actionButtonStyle);
    }

    get logoSrc() {
        return isHTTPSUrl(this.iframeParams.logoSrc) ? this.iframeParams.logoSrc : null;
    }

    get logoAlt() {
        return this.iframeParams.logoAlt;
    }

    get fonts() {
        return ensureArray(this.iframeParams.fonts).filter(isValidFontFace);
    }

    get merchantPageUrl() {
        return this.iframeParams.merchantPageUrl;
    }

    get widgetVisible() {
        return this.iframeParams.visible;
    }

    get screen() {
        return this.iframeParams.screen;
    }

    get positionBottom() {
        return this.iframeParams.positionBottom;
    }

    // TODO @branch these should be deprecated
    isInlineDonationFlow() {
        return this.gateType === GATE_TYPE.popup && this.panelType === PANEL_TYPE.donation;
    }

    isInlineSubscriptionFlow() {
        return this.gateType === GATE_TYPE.popup && this.panelType === PANEL_TYPE.subscribe;
    }

    handleFlowInline() {
        return this.gateType === GATE_TYPE.popup || this.iframeParams.inlineFlow;
    }
}

export const iFrameState = new IFrameState();
